import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  Tooltip,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import React, { useState } from "react";
import styled from "styled-components";

import { atomicConfig } from "../../../../../config";
import useCurrency from "../../../../../hooks/useCurrency";
import { call_api_auth } from "../../../../../utils/services";

const CompetitorsButton = styled(Button)`
  background-color: #f1f8ff !important;
  color: #3f97fe;
  font-size: 11px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 5px;
  border: 1px solid #f1f8ff;
  white-space: nowrap;
  margin-top: 8px;
  text-transform: none;
`;

const ProductCard = styled(Card)`
  display: flex;
  margin: 10px 0;
  padding: 12px;
  width: 100%;
  position: relative;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImageContainer = styled.div`
  flex: 0 0 100px;
  margin-right: 12px;
`;

const ProductImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
`;

const ProductDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
`;

const ProductTitle = styled(Typography)`
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
  color: #0f1111;
  max-width: 95%;

  &:hover {
    color: #007185;
    text-decoration: underline;
  }
`;

const ProductMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 6px 0;
`;

const ProductPrice = styled(Typography)`
  color: #b12704;
  font-size: 16px;
  font-weight: bold;
`;

const RatingsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const RatingsCount = styled.span`
  color: #007185;
  margin-left: 4px;
  font-size: 12px;
`;

const ProductInfo = styled.div`
  color: #565959;
  font-size: 12px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const UnitEconomics = ({
  unitEconomicsData,
  ppcPerUnit,
  initialPrice,
  onCalculationComplete,
  marketplace,
  asin,
  store,
}) => {
  const { showGBP, convertCurrency, convertToUsd, getCurrencySymbol } =
    useCurrency();
  // Competitors dialog state
  const [competitorsOpen, setCompetitorsOpen] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);

  const handleOpenCompetitors = async () => {
    setCompetitorsOpen(true);
    setCompetitorsLoading(true);
    try {
      const marketplaceValue = marketplace || "us";
      const storeValue = store?.replace("_client", "") || "";
      const url = `${atomicConfig.storeManagementServiceUrl}/product-competitors/${storeValue}?asin=${asin}&marketplace=${marketplaceValue}&maxResults=20`;
      const result = await call_api_auth(url, "GET");

      if (result && result.data) {
        setCompetitors(result.data);
      } else {
        setCompetitors([]);
      }
    } catch (error) {
      console.error(
        "Error fetching competitors:",
        error.message || "Something went wrong"
      );
      setCompetitors([]);
    }
    setCompetitorsLoading(false);
  };

  const handleCloseCompetitors = () => {
    setCompetitorsOpen(false);
  };

  const unitEconomicsBreakdown = (unitEconomicsData, inputPrice = 0) => {
    let price = inputPrice;
    let cogs = 0;
    let fulfillment = 0;
    let referral = 0;
    let storage = 0;
    let vatAmount = 0;
    let refundRate = 0;
    let refundRecoveryRate = 1;
    let hasChildren = false;
    let hasLowInventoryFee = false;
    let isDirectLIF = false; // Direct LIF vs inherited from children
    let pricePercentile = null;
    let competitorCount = 0;
    let fulfillmentFeePercentile = null;
    let fulfillmentSizeTier = null;
    let competitorsWithFeeData = 0;

    if (unitEconomicsData) {
      if (Object.keys(unitEconomicsData).length === 1) {
        //parent no children
        unitEconomicsData = Object.values(unitEconomicsData)[0];
      }
      if ("price" in unitEconomicsData) {
        //child
        if (unitEconomicsData) {
          if (price === 0) {
            price = unitEconomicsData.price;
            referral = unitEconomicsData.referral_fee;
          } else {
            if (price < 10) {
              referral = (price * 0.08).toFixed(2);
            } else {
              referral = (price * 0.15).toFixed(2);
            }
          }
          cogs = parseFloat(unitEconomicsData.cogs.toFixed(2));
          fulfillment = unitEconomicsData.fulfillment_info?.fee || 0;
          fulfillmentFeePercentile =
            unitEconomicsData.fulfillment_info?.percentile ?? null;
          fulfillmentSizeTier =
            unitEconomicsData.fulfillment_info?.size_tier ?? null;
          competitorsWithFeeData =
            unitEconomicsData.fulfillment_info?.comparison
              ?.competitors_with_data || 0;
          storage = unitEconomicsData.storage_fee || 0;
          vatAmount = unitEconomicsData.vat_amount || 0;
          refundRate = unitEconomicsData.refund_rate || 0;
          refundRecoveryRate = unitEconomicsData.refund_recovery_rate ?? 1;
          hasLowInventoryFee = unitEconomicsData.low_inventory_fee || false;
          isDirectLIF = unitEconomicsData.low_inventory_fee || false; // Direct LIF for child items
          pricePercentile = unitEconomicsData.price_percentile ?? null;
          competitorCount = unitEconomicsData.competitor_count || 0;
        }
      } else {
        //parent
        hasChildren = Object.values(unitEconomicsData).length > 1;
        Object.values(unitEconomicsData).forEach((value) => {
          const breakdown = unitEconomicsBreakdown(value);
          const p = breakdown[0];
          const c = breakdown[1];
          const f = breakdown[2];
          const r = breakdown[3];
          const s = breakdown[4];
          const lif = breakdown[6];
          const rr = breakdown[13];
          const rrr = breakdown[14];
          const v = breakdown[15];

          price += p;
          cogs += c;
          fulfillment += f;
          referral += r;
          storage += s;
          refundRate += rr;
          refundRecoveryRate += rrr;
          vatAmount += v;
          if (lif) hasLowInventoryFee = true; // If any child has LIF, mark parent as having LIF
          // Parent should never have isDirectLIF = true (it's always inherited)
        });
        price = (price / Object.keys(unitEconomicsData).length).toFixed(2);
        cogs = (cogs / Object.keys(unitEconomicsData).length).toFixed(2);
        fulfillment = (
          fulfillment / Object.keys(unitEconomicsData).length
        ).toFixed(2);
        referral = (referral / Object.keys(unitEconomicsData).length).toFixed(
          2
        );
        storage = (storage / Object.keys(unitEconomicsData).length).toFixed(2);
        vatAmount = (vatAmount / Object.keys(unitEconomicsData).length).toFixed(
          2
        );
        refundRate = refundRate / Object.keys(unitEconomicsData).length;
        refundRecoveryRate =
          refundRecoveryRate / Object.keys(unitEconomicsData).length;
      }
    }
    return [
      price,
      cogs,
      Number(fulfillment),
      Number(referral),
      Number(storage),
      hasChildren,
      hasLowInventoryFee,
      isDirectLIF,
      pricePercentile,
      competitorCount,
      fulfillmentFeePercentile,
      fulfillmentSizeTier,
      competitorsWithFeeData,
      Number(refundRate),
      Number(refundRecoveryRate),
      Number(vatAmount),
    ];
  };

  // Initial breakdown
  const initialBreakdown = unitEconomicsBreakdown(
    unitEconomicsData,
    initialPrice
  );

  // State variables for each piece of data
  const [price, setPrice] = useState(initialBreakdown[0]);
  const [cogs, setCogs] = useState(initialBreakdown[1]);
  const [fulfillment, setFulfillment] = useState(initialBreakdown[2]);
  const [referral, setReferral] = useState(initialBreakdown[3]);
  const [storage, setStorage] = useState(initialBreakdown[4]);
  const [vatAmount, setVatAmount] = useState(initialBreakdown[15]);
  const [hasChildren, setHasChildren] = useState(initialBreakdown[5]);
  const [hasLowInventoryFee, setHasLowInventoryFee] = useState(
    initialBreakdown[6]
  );
  const [isDirectLIF, setIsDirectLIF] = useState(initialBreakdown[7]);
  const pricePercentile = initialBreakdown[8];
  const competitorCount = initialBreakdown[9];
  const fulfillmentFeePercentile = initialBreakdown[10];
  const fulfillmentSizeTier = initialBreakdown[11];
  const competitorsWithFeeData = initialBreakdown[12];
  const [refundRate, setRefundRate] = useState(initialBreakdown[13]);
  const [refundRecoveryRate] = useState(initialBreakdown[14]);

  const [ppcpu, setPpcpu] = useState(ppcPerUnit);

  // What was typed in the price / PPC boxes, in the currency on screen. The
  // state above stays in USD; when the currency picker changes, a typed value
  // is re-expressed in the new currency so the box keeps matching the card.
  const [priceInput, setPriceInput] = useState("");
  const [ppcInput, setPpcInput] = useState("");
  const [inputsShowForeign, setInputsShowForeign] = useState(showGBP);
  const toInputText = (usd) =>
    String(parseFloat(Number(convertCurrency(usd, showGBP)).toFixed(2)));
  if (inputsShowForeign !== showGBP) {
    setInputsShowForeign(showGBP);
    if (priceInput !== "") setPriceInput(toInputText(price));
    if (ppcInput !== "") setPpcInput(toInputText(ppcpu));
  }

  const calcUnitEconomics = (unitEconomicsData) => {
    let profit = 0;
    if (unitEconomicsData) {
      if ("price" in unitEconomicsData) {
        if (unitEconomicsData) {
          // Calculate refund cost: recovery_rate * price * refund_rate
          const refundCost = refundRecoveryRate * price * refundRate;
          // Calculate profit after refund
          const profitBeforeRefund =
            price - cogs - fulfillment - referral - storage - ppcpu;
          profit = profitBeforeRefund - refundCost;
        }
      } else {
        Object.entries(unitEconomicsData).forEach(([_, value]) => {
          profit += calcUnitEconomics(value);
        });
        profit = profit / Object.keys(unitEconomicsData).length;
      }
    }
    onCalculationComplete(
      parseFloat(profit.toFixed(1).replace(/\.0$/, ""), 10)
    );
    return profit;
  };

  // Handle change event
  const handlePriceChange = (e) => {
    setPriceInput(e.target.value);
    const inputValue = convertToUsd(e.target.value);

    const breakdown = unitEconomicsBreakdown(unitEconomicsData, inputValue);
    const newPrice = breakdown[0];
    const newCogs = breakdown[1];
    const newFulfillment = breakdown[2];
    const newReferral = breakdown[3];
    const newStorage = breakdown[4];
    const newVatAmount = breakdown[15];
    const newHasChildren = breakdown[5];
    const newHasLowInventoryFee = breakdown[6];
    const newIsDirectLIF = breakdown[7];
    const newRefundRate = breakdown[13];

    // Update state
    setPrice(newPrice);
    setCogs(newCogs);
    setFulfillment(newFulfillment);
    setReferral(newReferral);
    setStorage(newStorage);
    setVatAmount(newVatAmount);
    setHasChildren(newHasChildren);
    setHasLowInventoryFee(newHasLowInventoryFee);
    setIsDirectLIF(newIsDirectLIF);
    setRefundRate(newRefundRate);
  };

  const handlePpcChange = (e) => {
    setPpcInput(e.target.value);
    if (e.target.value !== "") {
      setPpcpu(convertToUsd(e.target.value));
    } else {
      setPpcpu(ppcPerUnit);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            fontFamily: "Inter",
            fontWeight: 600,
            color: "black",
            fontSize: 24,
            textAlign: "left",
          }}
        >
          {getCurrencySymbol()}
          {convertCurrency(
            calcUnitEconomics(unitEconomicsData).toFixed(1).replace(/\.0$/, ""),
            showGBP
          )}
        </div>
        <div style={{ fontSize: 12, marginLeft: 3 }}>
          {"(" +
            ((calcUnitEconomics(unitEconomicsData) / price) * 100).toFixed(1) +
            "%)"}
        </div>
      </div>
      <div style={{ marginTop: "10px", lineHeight: "1.8" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ whiteSpace: "nowrap" }}>
            {hasChildren ? "avg " : ""}price: {getCurrencySymbol()}
          </span>
          {hasChildren ? (
            convertCurrency(price, showGBP)
          ) : (
            <TextField
              sx={{
                width: "60px",
                marginLeft: "5px",
              }}
              size="small"
              id="outlined-basic"
              placeholder={price != null ? convertCurrency(price, showGBP) : ""}
              variant="outlined"
              value={priceInput}
              onChange={handlePriceChange}
              InputProps={{
                style: { height: "20px", fontSize: "0.6rem" },
              }}
              inputProps={{
                style: { height: "20px", fontSize: "0.6rem" },
              }}
            />
          )}
          {!hasChildren && competitorCount >= 2 && pricePercentile != null ? (
            <Tooltip
              title={`Your price is higher than ${Math.round(
                pricePercentile
              )}% of your ${competitorCount} competitors`}
              placement="top"
              arrow
            >
              <Chip
                label={`>${Math.round(pricePercentile)}% of ${competitorCount}`}
                size="small"
                sx={{
                  marginLeft: 1,
                  height: "18px",
                  fontSize: "11px",
                  fontWeight: 500,
                  backgroundColor: "#e8f0fe",
                  color: "#1a73e8",
                  borderRadius: "12px",
                  "& .MuiChip-label": {
                    padding: "0 8px",
                  },
                  cursor: "help",
                  "&:hover": {
                    backgroundColor: "#d2e3fc",
                  },
                }}
              />
            </Tooltip>
          ) : !hasChildren && competitorCount < 2 ? (
            <span
              style={{ marginLeft: "8px", color: "#9aa0a6", fontSize: "12px" }}
            >
              -
            </span>
          ) : null}
        </div>
        {hasChildren ? "avg " : ""}cogs: -{getCurrencySymbol()}
        {convertCurrency(cogs, showGBP)}
        <br />
        {hasChildren ? "avg " : ""}refunds: -{getCurrencySymbol()}
        {convertCurrency(
          (refundRecoveryRate * price * refundRate).toFixed(2),
          showGBP
        )}
        <br />
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{hasChildren ? "avg " : ""}fulfillment: -</span>
          {fulfillmentSizeTier && !hasChildren ? (
            <Tooltip title={`Size tier: ${fulfillmentSizeTier}`} arrow>
              <span
                style={{
                  cursor: "help",
                  borderBottom: "1px dotted #666",
                }}
              >
                {getCurrencySymbol()}
                {convertCurrency(fulfillment, showGBP)}
              </span>
            </Tooltip>
          ) : (
            <span>
              {getCurrencySymbol()}
              {convertCurrency(fulfillment, showGBP)}
            </span>
          )}
          {!hasChildren &&
          competitorsWithFeeData >= 2 &&
          fulfillmentFeePercentile != null ? (
            <Tooltip
              title={
                competitorCount > competitorsWithFeeData
                  ? `Your fulfillment fee is lower than ${Math.round(
                      fulfillmentFeePercentile
                    )}% of ${competitorsWithFeeData} competitors (${
                      competitorCount - competitorsWithFeeData
                    } missing fee data)`
                  : `Your fulfillment fee is lower than ${Math.round(
                      fulfillmentFeePercentile
                    )}% of ${competitorsWithFeeData} competitors`
              }
              placement="top"
              arrow
            >
              <Chip
                label={`<${Math.round(
                  fulfillmentFeePercentile
                )}% of ${competitorsWithFeeData}`}
                size="small"
                sx={{
                  marginLeft: 1,
                  height: "18px",
                  fontSize: "11px",
                  fontWeight: 500,
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  borderRadius: "12px",
                  "& .MuiChip-label": {
                    padding: "0 8px",
                  },
                  cursor: "help",
                  "&:hover": {
                    backgroundColor: "#c8e6c9",
                  },
                }}
              />
            </Tooltip>
          ) : null}
          {hasLowInventoryFee && (
            <Tooltip
              title={
                isDirectLIF
                  ? "fulfillment was added a low-inventory-fee this week"
                  : "one or more variants have low-inventory-fees this week"
              }
              placement="top"
              arrow
            >
              <Chip
                label="LIF"
                size="small"
                sx={{
                  marginLeft: 1,
                  height: "16px",
                  fontSize: "9px",
                  fontWeight: "bold",
                  backgroundColor: isDirectLIF ? "#ffebee" : "#f5f5f5",
                  color: isDirectLIF ? "#d32f2f" : "#666666",
                  border: `1px solid ${isDirectLIF ? "#d32f2f" : "#666666"}`,
                  "& .MuiChip-label": {
                    padding: "0 4px",
                  },
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </div>
        {hasChildren ? "avg " : ""}referral: -{getCurrencySymbol()}
        {convertCurrency(referral, showGBP)}
        <br />
        {hasChildren ? "avg " : ""}storage: -{getCurrencySymbol()}
        {convertCurrency(storage, showGBP)}
        <br />
        {Number(vatAmount) > 0 && (
          <>
            {hasChildren ? "avg " : ""}VAT: -{getCurrencySymbol()}
            {convertCurrency(vatAmount, showGBP)}
            <br />
          </>
        )}
        {hasChildren ? "avg " : ""}PPC per unit: -{getCurrencySymbol()}
        {hasChildren ? (
          convertCurrency(ppcPerUnit, showGBP)
        ) : (
          <TextField
            sx={{
              width: "60px",
              marginTop: "-10px",
              marginLeft: "5px",
            }}
            size="small"
            id="outlined-basic"
            placeholder={convertCurrency(ppcPerUnit, showGBP)}
            variant="outlined"
            value={ppcInput}
            onChange={handlePpcChange}
            InputProps={{
              style: { height: "20px", fontSize: "0.6rem", marginTop: "8px" },
            }}
            inputProps={{
              style: { height: "20px", fontSize: "0.6rem" },
            }}
          />
        )}
      </div>

      {asin && (
        <CompetitorsButton onClick={handleOpenCompetitors}>
          Competitors
        </CompetitorsButton>
      )}

      <Dialog
        open={competitorsOpen}
        onClose={handleCloseCompetitors}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Top Competitors
          <IconButton
            aria-label="close"
            onClick={handleCloseCompetitors}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {competitorsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : competitors.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                color: "#666",
              }}
            >
              <Typography>No competitors found</Typography>
            </Box>
          ) : (
            competitors.map((competitor, index) => (
              <ProductCard key={competitor.asin || index}>
                <ProductImageContainer>
                  <ProductImage
                    src={competitor.image}
                    alt={competitor.title}
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/100?text=No+Image";
                    }}
                  />
                </ProductImageContainer>

                <ProductDetails>
                  <div>
                    <ProductTitle
                      onClick={() =>
                        window.open(
                          competitor.link ||
                            `https://www.amazon.${
                              marketplace === "ca"
                                ? "ca"
                                : marketplace === "mx"
                                ? "com.mx"
                                : marketplace === "uk"
                                ? "co.uk"
                                : marketplace === "de"
                                ? "de"
                                : "com"
                            }/dp/${competitor.asin}`,
                          "_blank"
                        )
                      }
                    >
                      {competitor.title}
                    </ProductTitle>

                    <ProductMetadata>
                      <RatingsContainer>
                        <Rating
                          value={competitor.rating || 0}
                          readOnly
                          precision={0.1}
                          size="small"
                        />
                        <RatingsCount>
                          ({competitor.ratings_total?.toLocaleString() || 0})
                        </RatingsCount>
                      </RatingsContainer>

                      <ProductPrice>
                        {competitor.price != null
                          ? `${getCurrencySymbol()}${convertCurrency(
                              competitor.price.toFixed(2),
                              showGBP
                            )}`
                          : "N/A"}
                      </ProductPrice>
                    </ProductMetadata>

                    <ProductInfo>
                      <span>ASIN: {competitor.asin}</span>
                      {competitor.recent_sales !== undefined && (
                        <span>
                          • Units Sold:{" "}
                          {competitor.recent_sales?.toLocaleString()}
                        </span>
                      )}
                      <span
                        style={
                          (competitor.fulfillment_info?.fee ??
                            competitor.fulfillment_fee) != null &&
                          fulfillment > 0 &&
                          (competitor.fulfillment_info?.fee ??
                            competitor.fulfillment_fee) < fulfillment
                            ? {
                                fontWeight: 700,
                                color: "green",
                                backgroundColor: "rgba(34, 197, 94, 0.12)",
                                boxShadow: "0 0 10px rgba(34, 197, 94, 0.22)",
                                borderRadius: "4px",
                                px: 0.5,
                              }
                            : undefined
                        }
                      >
                        • FBA Fee:{" "}
                        {(() => {
                          const fee =
                            competitor.fulfillment_info?.fee ??
                            competitor.fulfillment_fee;
                          return fee != null
                            ? `${getCurrencySymbol()}${convertCurrency(
                                fee.toFixed(2),
                                showGBP
                              )}`
                            : "N/A";
                        })()}
                      </span>
                    </ProductInfo>
                  </div>
                </ProductDetails>
              </ProductCard>
            ))
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { UnitEconomics };
